import { Link } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "@/api/client";
import { useLinkStoreOfferIntegration, useUnlinkStoreOfferIntegration } from "@/api/mutations";
import type { StoreDetail, StoreOfferIntegration } from "@/api/types";
import { usePermissions } from "@/auth/permissions";
import { Section } from "@/components/feedback/key-value";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { TurnOnOfferApi } from "@/features/integrations/turn-on-offer-api";
import { formErrorsFrom } from "@/lib/form-errors";

/**
 * The organization's offer API integrations for one store: turn each one on for the store, with
 * the store's id at the provider (external store id) when the connector uses one.
 */
export function StoreOfferIntegrations({ store }: { store: StoreDetail }) {
  const list = store.offerIntegrations ?? [];
  return (
    <Section title="Offer API" className="[&>div]:p-0">
      {!store.offerApi ? (
        <Alert className="m-3 w-auto">
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
            <span>The Offer API is off for {store.organization.name}: integrations turned on here only run once it's on.</span>
            <TurnOnOfferApi organizationId={store.organization.id} />
          </AlertDescription>
        </Alert>
      ) : null}
      {list.length === 0 ? (
        <p className="px-3 py-3 text-muted-foreground text-xs">
          {store.organization.name} has no offer API integrations yet. Create one under{" "}
          <Link to="/integrations" className="underline">
            Integrations
          </Link>
          .
        </p>
      ) : (
        <ul className="divide-y">
          {list.map((i) => (
            <Row key={i.integrationId} storeId={store.id} integration={i} />
          ))}
        </ul>
      )}
    </Section>
  );
}

function Row({ storeId, integration: i }: { storeId: string; integration: StoreOfferIntegration }) {
  const { can } = usePermissions();
  const manage = can("integrations:manage");
  const link = useLinkStoreOfferIntegration(storeId);
  const unlink = useUnlinkStoreOfferIntegration(storeId);
  const [externalId, setExternalId] = useState(i.externalStoreId ?? "");
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const field = i.externalStoreIdField;
  const busy = link.isPending || unlink.isPending;

  const save = (e?: FormEvent) => {
    e?.preventDefault();
    if (field?.required && !externalId.trim()) return setError(`Enter the ${field.label}`);
    setError(null);
    link.mutate(
      { integrationId: i.integrationId, externalStoreId: externalId.trim() || null },
      {
        onSuccess: () => {
          toast.success(`${i.name} is on for this store`);
          setEditing(false);
        },
        onError: (err) => {
          const { fields, form } = formErrorsFrom(err);
          setError(fields.externalStoreId ?? form);
        },
      },
    );
  };
  const toggle = (on: boolean) => {
    if (on) {
      // A connector that needs the store's id asks for it before turning on.
      if (field) setEditing(true);
      else save();
      return;
    }
    unlink.mutate(i.integrationId, {
      onSuccess: () => toast.success(`${i.name} is off for this store`),
      onError: (err) => toast.error("Couldn't turn it off", { description: errorMessage(err) }),
    });
  };

  return (
    <li className="space-y-2 px-3 py-2.5 text-xs" data-testid={`store-offer-${i.integrationId}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <Link to="/integrations/$integrationId" params={{ integrationId: i.integrationId }} className="font-medium hover:underline">
            {i.name}
          </Link>
          <span className="text-muted-foreground"> · {i.driverLabel}</span>
          {!i.active ? <span className="ml-1 text-warning">· integration inactive</span> : null}
          {i.linked && i.externalStoreId ? (
            <div className="text-muted-foreground">
              {field?.label ?? "External store id"}: <span className="font-mono">{i.externalStoreId}</span>
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {i.linked && field && manage && !editing ? (
            <Button size="xs" variant="ghost" onClick={() => setEditing(true)}>
              Edit id
            </Button>
          ) : null}
          <Switch
            checked={i.linked || editing}
            disabled={!manage || busy}
            onCheckedChange={(on) => (editing && !on ? setEditing(false) : toggle(on))}
            aria-label={`Use ${i.name} for this store`}
          />
        </div>
      </div>
      {editing && field ? (
        <form onSubmit={save} className="flex flex-wrap items-start gap-2" noValidate>
          <div className="min-w-48 flex-1">
            <Input
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              placeholder={field.label}
              aria-label={field.label}
              aria-invalid={Boolean(error)}
              className="h-8"
            />
            <p className={error ? "mt-1 text-danger" : "mt-1 text-muted-foreground"}>{error ?? `${field.help}${field.required ? "" : " Optional."}`}</p>
          </div>
          <Button type="submit" size="sm" disabled={busy}>
            {busy ? <Spinner data-icon="inline-start" /> : null}
            {i.linked ? "Save" : "Turn on"}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </form>
      ) : null}
    </li>
  );
}
